import { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  ShowChart,
} from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import type { PortfolioSummary, Position, Balance } from '../types';
import {
  fetchPortfolioSummary,
  fetchPositions,
  fetchBalances,
} from '../services/api';

const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const fmtNum = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatCard({ label, value, sub, icon, color }: {
  label: string; value: string; sub?: string; icon: React.ReactNode; color: string;
}) {
  return (
    <Card sx={{ flex: 1, minWidth: 160 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
          </Typography>
          <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color={sub.startsWith('+') || sub.startsWith('-') ? color : 'text.secondary'}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// Mock equity curve data (replace with real API data)
function generateEquityCurve() {
  let equity = 50000;
  return Array.from({ length: 60 }, (_, i) => {
    equity = equity * (1 + (Math.random() - 0.47) * 0.04);
    return { day: i + 1, equity: Math.round(equity) };
  });
}

interface Props { onNotify: (msg: string) => void; }

export default function PortfolioPanel({}: Props) {
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, b] = await Promise.all([
        fetchPortfolioSummary().catch(() => null),
        fetchPositions().catch(() => []),
        fetchBalances().catch(() => []),
      ]);
      setSummary(s);
      setPositions(p);
      setBalances(b);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) return <LinearProgress sx={{ mb: 2 }} />;

  // Use real data if available, else fallbacks
  const equity = summary?.totalEquity ?? 50_000;
  const totalPnl = summary?.totalPnl ?? 3240.5;
  const totalPnlPct = summary?.totalPnlPct ?? 6.93;
  const unrealized = summary?.unrealizedPnl ?? 1240.0;
  const realized = summary?.realizedPnl ?? 2000.5;
  const winRate = summary?.winRate ?? 0.62;
  const sharpe = summary?.Sharpe ?? 1.45;
  const maxDD = summary?.maxDrawdown ?? 0.12;
  const usedMargin = summary?.usedMargin ?? 12_000;
  const totalMargin = summary?.totalMargin ?? 50_000;
  const equityCurve = generateEquityCurve();

  return (
    <>
      {/* ── KPI Cards ── */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Total Equity"
              value={fmt.format(equity)}
              sub={`${totalPnl >= 0 ? '+' : ''}${fmt.format(totalPnl)} (${totalPnlPct >= 0 ? '+' : ''}${totalPnlPct.toFixed(2)}%)`}
              icon={<AccountBalance sx={{ fontSize: 18 }} />}
              color={totalPnl >= 0 ? 'success.main' : 'error.main'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Unrealized P&L"
              value={fmt.format(unrealized)}
              sub={`${unrealized >= 0 ? '+' : ''}${fmt.format(unrealized)}`}
              icon={<ShowChart sx={{ fontSize: 18 }} />}
              color={unrealized >= 0 ? 'success.main' : 'error.main'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Win Rate"
              value={`${(winRate * 100).toFixed(1)}%`}
              sub={`${sharpe.toFixed(2)} Sharpe`}
              icon={<TrendingUp sx={{ fontSize: 18 }} />}
              color="primary.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Max Drawdown"
              value={`${(maxDD * 100).toFixed(1)}%`}
              sub="Portfolio limit: 15%"
              icon={<TrendingDown sx={{ fontSize: 18 }} />}
              color={maxDD > 0.10 ? 'warning.main' : 'text.secondary'}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* ── Equity Curve ── */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Equity Curve (60d)
            </Typography>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={equityCurve}>
                <defs>
                  <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={false} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: '#8b949e' }} axisLine={false} tickLine={false} width={45} />
                <Tooltip formatter={(v: number) => [fmt.format(v), 'Equity']} contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid #30363d', borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="equity" stroke="#6366f1" strokeWidth={2} fill="url(#eqGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* ── Margin Utilisation ── */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Margin Utilisation
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Used</Typography>
                  <Typography variant="caption">{fmt.format(usedMargin)}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={(usedMargin / totalMargin) * 100} sx={{ height: 8, '& .MuiLinearProgress-bar': { backgroundColor: '#f59e0b' } }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Available</Typography>
                  <Typography variant="caption">{fmt.format(totalMargin - usedMargin)}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={((totalMargin - usedMargin) / totalMargin) * 100} sx={{ height: 8, '& .MuiLinearProgress-bar': { backgroundColor: '#10b981' } }} />
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Realised P&L</Typography>
                <Typography variant="caption" color={realized >= 0 ? 'success.main' : 'error.main'}>
                  {realized >= 0 ? '+' : ''}{fmt.format(realized)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Unrealised P&L</Typography>
                <Typography variant="caption" color={unrealized >= 0 ? 'success.main' : 'error.main'}>
                  {unrealized >= 0 ? '+' : ''}{fmt.format(unrealized)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* ── Positions Table ── */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Open Positions ({positions.length})
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Side</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Entry</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">P&L</TableCell>
                    <TableCell align="right">P&L %</TableCell>
                    <TableCell align="right">Lev</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="caption" color="text.secondary" sx={{ py: 2, display: 'block' }}>
                          No open positions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {positions.map((pos) => (
                    <TableRow key={pos.id} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell><Chip label={pos.symbol} size="small" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem' }} /></TableCell>
                      <TableCell align="right">
                        <Chip label={pos.side.toUpperCase()} size="small" color={pos.side === 'long' ? 'success' : 'error'} sx={{ fontSize: '0.65rem', height: 20 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>{pos.quantity.toFixed(4)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>${fmtNum.format(pos.entryPrice)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>${fmtNum.format(pos.currentPrice)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', color: pos.pnl >= 0 ? 'success.main' : 'error.main' }}>
                        {pos.pnl >= 0 ? '+' : ''}{fmt.format(pos.pnl)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', color: pos.pnlPct >= 0 ? 'success.main' : 'error.main' }}>
                        {pos.pnlPct >= 0 ? '+' : ''}{pos.pnlPct.toFixed(2)}%
                      </TableCell>
                      <TableCell align="right"><Chip label={`${pos.leverage}×`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#30363d' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* ── Balances ── */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Asset Balances
            </Typography>
            <Stack spacing={1}>
              {balances.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No balances</Typography>
              ) : (
                balances.map((bal) => (
                  <Box key={bal.asset} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{bal.asset}</Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        {bal.free.toFixed(4)}{bal.locked > 0 ? ` +${bal.locked.toFixed(4)}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{fmt.format(bal.usdValue)}</Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
}
